import { useState, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { 
  Sparkles, 
  ArrowRight, 
  Copy, 
  Check, 
  ChevronLeft,
  Loader2,
  FileText,
  BarChart3,
  Code,
  Image,
  Lightbulb,
  Pencil,
  RefreshCw,
  TrendingUp,
  Users,
  Megaphone,
  HeadphonesIcon,
  Briefcase,
  ChevronDown,
  Zap,
  Download,
  Share2,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confetti celebration
const triggerConfetti = () => {
  const duration = 2000;
  const end = Date.now() + duration;

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors: ['#6366f1', '#ec4899', '#22c55e']
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors: ['#6366f1', '#ec4899', '#22c55e']
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  };
  frame();
};

// Analytics helper
const trackEvent = async (eventType, data = {}) => {
  try {
    await axios.post(`${API}/track?event_type=${eventType}`, data);
    if (window.gtag) {
      window.gtag('event', eventType, data);
    }
  } catch (e) {
    console.log('Analytics tracking:', eventType);
  }
};

// Animation variants
const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.3, ease: "easeOut" }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0 }
};

// Icon mapping
const ICON_MAP = {
  TrendingUp,
  Users,
  Megaphone,
  HeadphonesIcon,
  Briefcase
};

// Use case examples
const USE_CASES = [
  { icon: FileText, label: "Rédaction", example: "Écris-moi un post LinkedIn" },
  { icon: BarChart3, label: "Analyse", example: "Analyse ce marché" },
  { icon: Lightbulb, label: "Stratégie", example: "Trouve-moi une idée de business" },
  { icon: Code, label: "Code", example: "Crée-moi une app" },
  { icon: Image, label: "Image", example: "Fais-moi un visuel" },
];

// Main App Component
function App() {
  const [step, setStep] = useState(1);
  const [userInput, setUserInput] = useState("");
  const [classification, setClassification] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentRound, setCurrentRound] = useState(1);
  const [brief, setBrief] = useState("");
  const [promptOutput, setPromptOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(null);
  
  // Fun features
  const [roastMode, setRoastMode] = useState(false);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const scoreCardRef = useRef(null);
  
  // Queue status
  const [queueStatus, setQueueStatus] = useState(null);
  const [queuePolling, setQueuePolling] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState({});
  const [selectedMetier, setSelectedMetier] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateContext, setTemplateContext] = useState(null);

  // Trigger confetti when score is high
  useEffect(() => {
    if (promptOutput && promptOutput.global_score >= 85) {
      triggerConfetti();
    }
  }, [promptOutput]);

  // Fetch templates on mount + track session
  useEffect(() => {
    const init = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data.templates);
        trackEvent('session_start', { timestamp: new Date().toISOString() });
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    init();
  }, []);

  // Poll queue status when loading
  useEffect(() => {
    let interval;
    if (queuePolling) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/queue/status`);
          setQueueStatus(response.data);
        } catch (e) {
          console.log("Queue status check failed");
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [queuePolling]);

  // Step 1: Analyze user input
  const handleAnalyze = useCallback(async () => {
    if (!userInput.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze`, { text: userInput });
      setClassification(response.data);
      
      // Get clarification questions
      const clarifyResponse = await axios.post(`${API}/clarify`, {
        original_text: userInput,
        classification: response.data,
        round: 1
      });
      
      if (clarifyResponse.data.can_proceed || clarifyResponse.data.questions.length === 0) {
        await handleGenerate(response.data, templateContext ? { ...templateContext } : {});
      } else {
        // Pre-fill answers from template context
        if (templateContext) {
          setAnswers(templateContext);
        }
        setQuestions(clarifyResponse.data.questions);
        setStep(2);
      }
    } catch (error) {
      console.error("Error analyzing:", error);
    } finally {
      setLoading(false);
    }
  }, [userInput, templateContext]);

  // Step 2: Submit answers
  const handleSubmitAnswers = useCallback(async () => {
    setLoading(true);
    try {
      const allAnswers = { ...answers };
      
      const clarifyResponse = await axios.post(`${API}/clarify`, {
        original_text: userInput,
        classification: classification,
        previous_answers: allAnswers,
        round: currentRound + 1
      });
      
      if (clarifyResponse.data.can_proceed || currentRound >= 2 || clarifyResponse.data.questions.length === 0) {
        await handleGenerate(classification, allAnswers);
      } else {
        setQuestions(clarifyResponse.data.questions);
        setCurrentRound(currentRound + 1);
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
    } finally {
      setLoading(false);
    }
  }, [answers, userInput, classification, currentRound]);

  // Step 3: Generate prompts
  const handleGenerate = async (classificationData, answersData) => {
    setLoading(true);
    setQueuePolling(true);
    setQueueStatus(null);
    
    try {
      const response = await axios.post(`${API}/generate-prompt`, {
        original_text: userInput,
        classification: classificationData || classification,
        answers: answersData || answers,
        brief: brief || null,
        roast_mode: roastMode
      });
      
      setPromptOutput(response.data);
      setBrief(response.data.brief);
      setStep(4);
    } catch (error) {
      console.error("Error generating:", error);
      if (error.response?.status === 503) {
        alert("Le service est très sollicité. Veuillez réessayer dans quelques instants.");
      }
    } finally {
      setLoading(false);
      setQueuePolling(false);
      setQueueStatus(null);
    }
  };

  // Copy to clipboard
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    trackEvent('prompt_copied', { version: id });
    setTimeout(() => setCopied(null), 2000);
  };

  // Select template
  const handleSelectTemplate = (template) => {
    setUserInput(template.prefill);
    setTemplateContext(template.context || {});
    setShowTemplates(false);
    setSelectedMetier(null);
    trackEvent('template_used', { template_id: template.id, template_title: template.title });
  };

  // Reset everything
  const handleReset = () => {
    setStep(1);
    setUserInput("");
    setClassification(null);
    setQuestions([]);
    setAnswers({});
    setCurrentRound(1);
    setBrief("");
    setPromptOutput(null);
    setShowShareMenu(null);
    setSelectedMetier(null);
    setTemplateContext(null);
    setShowBeforeAfter(false);
  };

  // Generate Score Card as image
  const generateScoreCard = () => {
    if (!promptOutput) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 400;
    
    // Background
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Gradient accent
    const gradient = ctx.createLinearGradient(0, 0, 600, 0);
    gradient.addColorStop(0, '#6366f1');
    gradient.addColorStop(1, '#ec4899');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 4);
    
    // Title
    ctx.fillStyle = '#f8fafc';
    ctx.font = 'bold 28px system-ui';
    ctx.fillText('PromptPilot', 40, 60);
    
    // Score circle
    const scoreColor = promptOutput.global_score >= 80 ? '#22c55e' : promptOutput.global_score >= 50 ? '#eab308' : '#ef4444';
    ctx.beginPath();
    ctx.arc(500, 100, 50, 0, 2 * Math.PI);
    ctx.fillStyle = scoreColor + '20';
    ctx.fill();
    ctx.strokeStyle = scoreColor;
    ctx.lineWidth = 4;
    ctx.stroke();
    
    ctx.fillStyle = scoreColor;
    ctx.font = 'bold 36px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText(promptOutput.global_score.toString(), 500, 112);
    ctx.font = '14px system-ui';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('/100', 500, 135);
    
    // Score details
    ctx.textAlign = 'left';
    ctx.font = '14px system-ui';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('Score de qualité du prompt', 40, 100);
    
    // Bars
    let y = 160;
    promptOutput.score_details.forEach((detail) => {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '14px system-ui';
      ctx.fillText(detail.dimension, 40, y);
      ctx.fillText(`${detail.score}/20`, 520, y);
      
      // Bar background
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(40, y + 10, 480, 8);
      
      // Bar fill
      const barColor = detail.score >= 16 ? '#22c55e' : detail.score >= 10 ? '#eab308' : '#ef4444';
      ctx.fillStyle = barColor;
      ctx.fillRect(40, y + 10, (detail.score / 20) * 480, 8);
      
      y += 45;
    });
    
    // Footer
    ctx.fillStyle = '#64748b';
    ctx.font = '12px system-ui';
    ctx.fillText('Généré avec PromptPilot • promptpilot.com', 40, 375);
    
    // Download
    const link = document.createElement('a');
    link.download = `promptpilot-score-${promptOutput.global_score}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    trackEvent('score_card_downloaded', { score: promptOutput.global_score });
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 80) return "bg-score-high";
    if (score >= 50) return "bg-score-medium";
    return "bg-score-low";
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="absolute inset-0 grid-background opacity-30 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center neon-glow">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground">
              PromptPilot
            </h1>
          </div>
          {step > 1 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleReset}
              data-testid="reset-button"
              className="text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recommencer
            </Button>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Input */}
          {step === 1 && (
            <motion.div
              key="input"
              {...fadeIn}
              className="space-y-8"
            >
              <div className="text-center space-y-4">
                <h2 className="font-heading text-4xl font-bold text-foreground">
                  Transformez vos idées en <span className="text-gradient">prompts parfaits</span>
                </h2>
                <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                  Décrivez ce que vous voulez demander à l'IA, nous vous aidons à clarifier votre besoin.
                </p>
              </div>

              {/* Templates métier */}
              <div className="space-y-3">
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  data-testid="toggle-templates"
                  className="w-full glassmorphism rounded-lg p-4 flex items-center justify-between hover:border-primary/50 transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-accent" />
                    <span className="font-medium text-foreground">Templates métier</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent">
                      {Object.keys(templates).length} métiers
                    </span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showTemplates && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2">
                        {Object.values(templates).map((metier) => {
                          const IconComponent = ICON_MAP[metier.icon] || Briefcase;
                          return (
                            <button
                              key={metier.id}
                              data-testid={`metier-${metier.id}`}
                              onClick={() => setSelectedMetier(selectedMetier === metier.id ? null : metier.id)}
                              className={`p-3 rounded-lg border transition-all duration-300 text-center ${
                                selectedMetier === metier.id 
                                  ? 'border-primary bg-primary/10' 
                                  : 'border-border/50 hover:border-primary/50 bg-black/20'
                              }`}
                            >
                              <IconComponent 
                                className="w-5 h-5 mx-auto mb-1" 
                                style={{ color: metier.color }}
                              />
                              <span className="text-xs font-medium text-foreground">{metier.name}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Template list for selected metier */}
                      <AnimatePresence>
                        {selectedMetier && templates[selectedMetier] && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-3 space-y-2"
                          >
                            {templates[selectedMetier].templates.map((template) => (
                              <button
                                key={template.id}
                                data-testid={`template-${template.id}`}
                                onClick={() => handleSelectTemplate(template)}
                                className="w-full p-4 rounded-lg border border-border/50 bg-black/30 hover:border-primary/50 transition-all duration-300 text-left group"
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
                                      {template.title}
                                    </h4>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {template.description}
                                    </p>
                                  </div>
                                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                                </div>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Main input */}
              <div className="glassmorphism rounded-xl p-6 space-y-4">
                {templateContext && (
                  <div className="flex items-center gap-2 text-sm text-accent">
                    <Zap className="w-4 h-4" />
                    <span>Template appliqué - contexte pré-rempli</span>
                    <button 
                      onClick={() => { setTemplateContext(null); setUserInput(""); }}
                      className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                    >
                      Effacer
                    </button>
                  </div>
                )}
                <Textarea
                  data-testid="user-input"
                  placeholder="Décrivez ce que vous voulez demander à l'IA..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  className="min-h-[120px] bg-black/50 border-input resize-none text-foreground placeholder:text-muted-foreground focus:ring-primary"
                />
                
                {/* Roast Mode Toggle */}
                <div className="flex items-center justify-between py-2 px-1">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-accent" />
                    <span className="text-sm text-foreground">Mode taquin</span>
                    <span className="text-xs text-muted-foreground">(l'IA commente votre demande)</span>
                  </div>
                  <Switch 
                    checked={roastMode} 
                    onCheckedChange={setRoastMode}
                    data-testid="roast-mode-toggle"
                  />
                </div>
                
                <Button
                  data-testid="analyze-button"
                  onClick={handleAnalyze}
                  disabled={!userInput.trim() || loading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground neon-glow-strong transition-all duration-300 hover:scale-[1.02]"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-5 h-5 mr-2" />
                  )}
                  Améliorer ma demande
                </Button>

                {/* Queue Status Display */}
                {loading && queueStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glassmorphism rounded-lg p-4 text-center space-y-2 mt-4"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-foreground font-medium">
                        Analyse en cours...
                      </span>
                    </div>
                    {queueStatus.queue_length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <span className="text-primary font-mono">{queueStatus.currently_processing}</span> requêtes en traitement
                        <span> • <span className="text-accent font-mono">{queueStatus.queue_length}</span> en attente</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Use case examples */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="show"
                className="space-y-4"
              >
                <p className="text-sm text-muted-foreground text-center">Exemples rapides</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {USE_CASES.map((useCase, index) => (
                    <motion.button
                      key={index}
                      variants={staggerItem}
                      data-testid={`usecase-${useCase.label.toLowerCase()}`}
                      onClick={() => { setUserInput(useCase.example); setTemplateContext(null); }}
                      className="glassmorphism rounded-lg p-4 text-center hover:border-primary/50 transition-all duration-300 card-hover group"
                    >
                      <useCase.icon className="w-6 h-6 mx-auto mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
                      <span className="text-sm font-medium text-foreground">{useCase.label}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Step 2: Clarification */}
          {step === 2 && (
            <motion.div
              key="clarify"
              {...fadeIn}
              className="space-y-8"
            >
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStep(1)}
                  data-testid="back-to-input"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Quelques précisions</h2>
                  <p className="text-muted-foreground">Pour affiner votre prompt</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Progress value={(currentRound / 2) * 100} className="h-2" />
                </div>
                <span className="text-sm font-mono text-muted-foreground">
                  Tour {currentRound}/2
                </span>
              </div>

              {classification && (
                <div className="glassmorphism rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Type détecté</span>
                    <span className="px-2 py-1 rounded bg-primary/20 text-primary text-sm font-medium">
                      {classification.type}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-foreground">{classification.intention}</span>
                  </div>
                </div>
              )}

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="show"
                className="space-y-4"
              >
                {questions.map((q, index) => (
                  <motion.div
                    key={q.id}
                    variants={staggerItem}
                    className="glassmorphism rounded-lg p-5 space-y-3"
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-primary/20 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <label className="text-foreground font-medium">{q.question}</label>
                    </div>
                    <Input
                      data-testid={`question-${q.id}`}
                      placeholder="Votre réponse..."
                      value={answers[q.field] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.field]: e.target.value })}
                      className="bg-black/50 border-input text-foreground placeholder:text-muted-foreground"
                    />
                  </motion.div>
                ))}
              </motion.div>

              <Button
                data-testid="submit-answers-button"
                onClick={handleSubmitAnswers}
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground neon-glow-strong"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="w-5 h-5 mr-2" />
                )}
                Continuer
              </Button>

              {/* Queue Status Display */}
              {loading && queueStatus && queueStatus.queue_length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glassmorphism rounded-lg p-4 text-center space-y-2"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-foreground font-medium">
                      Génération en cours...
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="text-primary font-mono">{queueStatus.currently_processing}</span> requêtes en traitement
                    {queueStatus.queue_length > 0 && (
                      <span> • <span className="text-accent font-mono">{queueStatus.queue_length}</span> en attente</span>
                    )}
                  </div>
                  <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: queueStatus.capacity_used }}
                    />
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Step 3: Summary */}
          {step === 3 && (
            <motion.div
              key="summary"
              {...fadeIn}
              className="space-y-8"
            >
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStep(2)}
                  data-testid="back-to-clarify"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Voici ce que j'ai compris</h2>
                  <p className="text-muted-foreground">Vous pouvez modifier si nécessaire</p>
                </div>
              </div>

              <div className="glassmorphism rounded-xl p-6 space-y-4">
                <Textarea
                  data-testid="brief-edit"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  className="min-h-[150px] bg-black/50 border-input resize-none text-foreground"
                />
                <Button
                  data-testid="generate-button"
                  onClick={() => handleGenerate(classification, answers)}
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground neon-glow-strong"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-5 h-5 mr-2" />
                  )}
                  Générer le prompt final
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Results */}
          {step === 4 && promptOutput && (
            <motion.div
              key="results"
              {...fadeIn}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">
                    Vos prompts sont prêts 
                    {promptOutput.global_score >= 85 && <span className="ml-2">🎉</span>}
                  </h2>
                  <p className="text-muted-foreground">Choisissez la version qui vous convient</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(3)}
                  data-testid="edit-brief-button"
                  className="border-input hover:bg-secondary"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </div>

              {/* Roast comment if enabled */}
              {promptOutput.roast && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glassmorphism rounded-lg p-4 border-l-4 border-accent"
                >
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground italic">{promptOutput.roast}</p>
                  </div>
                </motion.div>
              )}

              {/* Before/After Toggle */}
              <div className="flex items-center justify-between glassmorphism rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">Avant / Après</span>
                  <span className="text-xs text-muted-foreground">Voir la transformation</span>
                </div>
                <Switch 
                  checked={showBeforeAfter} 
                  onCheckedChange={setShowBeforeAfter}
                  data-testid="before-after-toggle"
                />
              </div>

              {/* Before/After Comparison */}
              {showBeforeAfter && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid md:grid-cols-2 gap-4"
                >
                  <div className="glassmorphism rounded-lg p-4 border-l-4 border-red-500/50">
                    <p className="text-xs font-mono text-red-400 mb-2 uppercase tracking-wider">Avant</p>
                    <p className="text-sm text-muted-foreground">{userInput}</p>
                  </div>
                  <div className="glassmorphism rounded-lg p-4 border-l-4 border-green-500/50">
                    <p className="text-xs font-mono text-green-400 mb-2 uppercase tracking-wider">Après</p>
                    <p className="text-sm text-foreground">{promptOutput.brief}</p>
                  </div>
                </motion.div>
              )}

              {/* Score Dashboard */}
              <div ref={scoreCardRef} className="glassmorphism rounded-xl p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-lg text-foreground">Score de qualité</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-3xl font-bold font-mono ${promptOutput.global_score >= 80 ? 'text-score-high' : promptOutput.global_score >= 50 ? 'text-score-medium' : 'text-score-low'}`}>
                        {promptOutput.global_score}
                      </span>
                      <span className="text-muted-foreground">/100</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={generateScoreCard}
                      data-testid="download-scorecard"
                      className="text-muted-foreground hover:text-foreground"
                      title="Télécharger la Score Card"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {promptOutput.score_details.map((detail, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{detail.dimension}</span>
                        <span className="text-muted-foreground font-mono">{detail.score}/20</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full score-bar ${getScoreColor(detail.score * 5)} rounded-full`}
                          style={{ width: `${(detail.score / 20) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {promptOutput.recommendations.length > 0 && promptOutput.global_score < 80 && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium text-foreground mb-2">Suggestions d'amélioration</p>
                    <ul className="space-y-1">
                      {promptOutput.recommendations.map((rec, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-accent">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Prompt Versions */}
              <Tabs defaultValue="detailed" className="space-y-4">
                <TabsList className="grid w-full grid-cols-3 bg-secondary relative z-50">
                  <TabsTrigger value="short" data-testid="tab-short" className="relative z-50">Court</TabsTrigger>
                  <TabsTrigger value="detailed" data-testid="tab-detailed" className="relative z-50">Détaillé</TabsTrigger>
                  <TabsTrigger value="expert" data-testid="tab-expert" className="relative z-50">Expert</TabsTrigger>
                </TabsList>

                {["short", "detailed", "expert"].map((type) => (
                  <TabsContent key={type} value={type} className="space-y-4">
                    <div className="glassmorphism rounded-xl p-6 space-y-4">
                      <pre className="whitespace-pre-wrap text-sm font-mono text-foreground leading-relaxed">
                        {type === "short" && promptOutput.prompt_short}
                        {type === "detailed" && promptOutput.prompt_detailed}
                        {type === "expert" && promptOutput.prompt_expert}
                      </pre>
                      <div className="pt-4 border-t border-border">
                        <Button
                          data-testid={`copy-${type}`}
                          onClick={() => handleCopy(
                            type === "short" ? promptOutput.prompt_short :
                            type === "detailed" ? promptOutput.prompt_detailed :
                            promptOutput.prompt_expert,
                            type
                          )}
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground neon-glow transition-all duration-300"
                        >
                          {copied === type ? (
                            <Check className="w-4 h-4 mr-2 text-white" />
                          ) : (
                            <Copy className="w-4 h-4 mr-2" />
                          )}
                          {copied === type ? "Copié dans le presse-papier !" : "Copier le prompt"}
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 mt-auto">
        <div className="max-w-5xl mx-auto px-6 py-4 text-center text-sm text-muted-foreground">
          PromptPilot • Transformez vos idées en prompts professionnels
        </div>
      </footer>
    </div>
  );
}

export default App;
